import React from 'react';
import { sanitizeHtml } from 'src/utilities/SanitizeHtml';
import { Box, SpaceBetween, Button, CopyToClipboard, LiveRegion } from '@amzn/awsui-components-core';
import { useChartSummaryContext } from './ChartSummaryContext';
import { useAuth } from 'src/components/context/AuthContextProvider';
import { FeedbackButtons } from './FeedbackButtons';
import LoadingBar from '@amzn/awsui-chat-components-core/loading-bar';

/**
 * Displays AI-generated chart summaries in a split panel format. Use this component to show
 * summaries generated from chart data, with loading states, feedback options, and clear functionality.
 * Requires ChartSummaryContext to manage summary state and operations.
 */
export const ChartSummaryPanel: React.FC = () => {
  const { summaries, isLoading, clearSummaries } = useChartSummaryContext();
  const { Alias } = useAuth();

  return (
    <Box padding="l">
      <SpaceBetween direction="vertical" size="m">
        <SpaceBetween direction="horizontal" size="xs" alignItems="center">
          {summaries.length > 0 && (
            <Button onClick={clearSummaries} iconName="remove">
              Clear All
            </Button>
          )}
        </SpaceBetween>

        {isLoading && (
          <Box padding="l">
            <LiveRegion>
              <SpaceBetween direction="vertical" size="s">
                <Box color="text-body-secondary">Generating the summary</Box>
                <div style={{ width: '90%' }}>
                  <LoadingBar variant="gen-ai" />
                </div>
              </SpaceBetween>
            </LiveRegion>
          </Box>
        )}

        {summaries.length === 0 && !isLoading ? (
          <Box color="text-status-inactive">No summaries available. Click 'Summarize' on any chart to view AI-generated insights.</Box>
        ) : (
          <SpaceBetween direction="vertical" size="l">
            {summaries.map((summary, index) => (
              <React.Fragment key={index}>
                <Box padding="s" variant="div">
                  <SpaceBetween direction="horizontal" size="xs" alignItems="center">
                    <h3>{summary.title}</h3>
                    <CopyToClipboard
                      copyButtonAriaLabel="Copy Summary"
                      copyErrorText="Failed to copy"
                      copySuccessText="Summary copied"
                      textToCopy={summary.content}
                      variant="icon"
                    />
                  </SpaceBetween>
                  <div dangerouslySetInnerHTML={{ __html: sanitizeHtml(summary.content) }} />
                  <Box padding={{ top: 'xs' }}>
                    <FeedbackButtons summaryId={summary.summaryId} userId={Alias} />
                  </Box>
                  <Box color="text-status-inactive" fontSize="body-s" padding={{ top: 's' }}>
                    Generated at {new Date(summary.timestamp).toLocaleTimeString()}
                  </Box>
                </Box>
                {index < summaries.length - 1 && (
                  <hr
                    style={{
                      border: 'none',
                      borderTop: '1px solid #06305bff',
                      margin: '16px 0'
                    }}
                  />
                )}
              </React.Fragment>
            ))}
          </SpaceBetween>
        )}
      </SpaceBetween>
    </Box>
  );
};
